import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { SupportAgent } from "@mui/icons-material";
import { useQueryClient } from "react-query";
import History from "../ConversationDetails/History";
import SearchResultsBulkBar from "../search/SearchResultsBulkBar";
import MergeRequestsDialog from "../search/MergeRequestsDialog";
import DistributeRequestsDialog from "../search/DistributeRequestsDialog";
import BulkChangeRequestsDialog from "../search/BulkChangeRequestsDialog";
import { useSearchResultsBulkPermissions } from "../../hooks/useSearchResultsBulkPermissions";
import { useUsers } from "../../hooks/useUsers";
import {
  CLIENT_REQUEST_EXPORT_COLUMNS,
  mapClientRequestsToExportRows,
  normalizeClientRequestsForHistory,
} from "../../utils/clientRequestHistory";

function EmptyRequestsState() {
  return (
    <Box
      sx={{
        py: 6,
        px: 3,
        textAlign: "center",
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <SupportAgent sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} />
      <Typography variant="subtitle1" color="text.secondary" fontWeight={600}>
        Nenhuma solicitação
      </Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mt: 0.75, maxWidth: 360, mx: "auto" }}>
        Clique em &quot;Nova solicitação&quot; no topo da página para iniciar um atendimento.
      </Typography>
    </Box>
  );
}

export default function ClientRequestsList({
  requests,
  clientName = "",
  onRequestsChanged,
}) {
  const items = useMemo(() => normalizeClientRequestsForHistory(requests), [requests]);
  const exportRows = useMemo(
    () => mapClientRequestsToExportRows(requests, { clientName }),
    [requests, clientName]
  );
  const selectableIds = useMemo(
    () => items.filter((item) => item?.id != null && !item.restricted).map((item) => item.id),
    [items]
  );

  const { canBulk, canExport } = useSearchResultsBulkPermissions();
  const showSelection = canBulk || canExport;
  const { data: operatorsData } = useUsers(1, 1000, "", "humanos", {
    order: "nome",
    enabled: canBulk,
  });
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const valid = new Set(selectableIds.map((id) => String(id)));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => valid.has(String(id)));
      if (
        next.length === prev.length &&
        next.every((id, index) => String(id) === String(prev[index]))
      ) {
        return prev;
      }
      return next;
    });
  }, [selectableIds]);

  const selectedCount = selectedIds.length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const handleToggleSelect = (id, checked) => {
    const key = String(id);
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.some((value) => String(value) === key)) return prev;
        return [...prev, id];
      }
      return prev.filter((value) => String(value) !== key);
    });
  };

  const handleToggleAll = (checked) => {
    setSelectedIds(checked ? [...selectableIds] : []);
  };

  const refreshRequests = () => {
    queryClient.invalidateQueries({ queryKey: ["client-details"] });
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    setSelectedIds([]);
    onRequestsChanged?.();
  };

  const notifyBulkSuccess = (message) => {
    setSnackbar({ open: true, message, severity: "success" });
    refreshRequests();
  };

  if (!items.length) {
    return <EmptyRequestsState />;
  }

  return (
    <Stack spacing={1.25}>
      {showSelection ? (
        <SearchResultsBulkBar
          selectedCount={selectedCount}
          selectedIds={selectedIds}
          rows={exportRows}
          columns={CLIENT_REQUEST_EXPORT_COLUMNS}
          exportTitle={clientName ? `Solicitações — ${clientName}` : "Solicitações do cliente"}
          exportFileName="solicitacoes_cliente"
          canBulk={canBulk}
          canExport={canExport}
          onMerge={() => canBulk && setMergeOpen(true)}
          onDistribute={() => canBulk && setDistributeOpen(true)}
          onChangeStatus={() => canBulk && setChangeStatusOpen(true)}
          onClearSelection={() => setSelectedIds([])}
        />
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          bgcolor: "background.paper",
          "& .MuiListItem-root": {
            px: showSelection ? { xs: 1.25, md: 2 } : { xs: 2, md: 2.5 },
            py: 1.75,
          },
          "& .MuiListItem-root:last-child": {
            borderBottom: "none",
          },
        }}
      >
        {showSelection ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: { xs: 1.25, md: 2 },
              py: 0.75,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              disabled={!selectableIds.length}
              onChange={(event) => handleToggleAll(event.target.checked)}
              inputProps={{ "aria-label": "Selecionar todas as solicitações" }}
              sx={{ p: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Selecionar todas
            </Typography>
          </Box>
        ) : null}
        <History
          history={items}
          selectable={showSelection}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
        />
      </Paper>

      {canBulk ? (
        <>
          <MergeRequestsDialog
            open={mergeOpen}
            selectedIds={selectedIds}
            onClose={() => setMergeOpen(false)}
            onMerged={() => notifyBulkSuccess("Solicitações unificadas com sucesso.")}
          />
          <DistributeRequestsDialog
            open={distributeOpen}
            selectedIds={selectedIds}
            operators={operatorsData?.users || []}
            onClose={() => setDistributeOpen(false)}
            onDistributed={() => notifyBulkSuccess("Solicitações distribuídas com sucesso.")}
          />
          <BulkChangeRequestsDialog
            open={changeStatusOpen}
            selectedIds={selectedIds}
            onClose={() => setChangeStatusOpen(false)}
            onChanged={() => notifyBulkSuccess("Solicitações alteradas com sucesso.")}
          />
        </>
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
